import { camelCase } from "lodash-es";
import { defineMessage, IntlShape } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import HasSourceObject from "@mapmycustomers/shared/types/base/HasSourceObject";
import { EntityTypesSupportingCustomSourceObject } from "@mapmycustomers/shared/types/entity";
import SourceObjectValue from "@mapmycustomers/shared/types/SourceObjectValue";
import {
  isEntityTypeSupportingCustomSourceObject,
  isNotEmpty,
} from "@mapmycustomers/shared/util/assert";

import getEntityTypeDisplayName from "@app/util/ui/getEntityTypeDisplayName";

// The display gate: an org "uses" source objects once it has more than the standard one. Pinned
// fields use mobile's rule (more than one source object) instead, in getPinnedSubtypeKeys.
export const hasCustomSourceObjects = (values: SourceObjectValue[]) =>
  values.some((item) => !item.isStandard);

export const getSourceObjectLabel = (
  intl: IntlShape | undefined,
  entityType: EntityTypesSupportingCustomSourceObject,
  values: SourceObjectValue[],
  key: HasSourceObject["sourceObject"] | undefined
): string | undefined => {
  if (key === undefined) {
    return undefined;
  }
  const match = values.find((item) => item.key === key);
  if (key === null) {
    // null IS the standard object per HasSourceObject, whatever the org's list holds — an org with
    // no integration reports no values at all, and its variants still need names.
    // Product wording is Company / Person, not the integration's label (e.g. Account / Contact).
    return intl ? getEntityTypeDisplayName(intl, entityType) : match?.label;
  }
  return match?.label ?? key;
};

// The reserved ICU key for the standard object. null IS the standard object, and ICU cannot switch
// on null, so every subtype message spells it this way. Never a real source object key.
export const STANDARD_SOURCE_OBJECT_INTL_KEY = "standard";

// ICU select arms cannot hold a dash but source object keys can, so every key is camelCased.
// An absent key deliberately reads as standard here — a plausible title beats an empty one.
export const getSourceObjectIntlKey = (key: HasSourceObject["sourceObject"] | undefined): string =>
  isNotEmpty(key) ? camelCase(key) : STANDARD_SOURCE_OBJECT_INTL_KEY;

// A type alias, not an interface: react-intl's `values` parameter is Record<string, ...>, and only
// a type alias gets the implicit index signature that makes it assignable without a spread.
export type SourceObjectMessageArgs = {
  label: string | undefined;
  sourceObject: string;
};

// Both arguments together: a select on sourceObject whose other arm lacks a label renders "".
// intl is optional for sagas — i18nService.getIntl() is undefined until the first locale loads.
export const getSourceObjectMessageArgs = (
  intl: IntlShape | undefined,
  entityType: EntityType | undefined,
  values: SourceObjectValue[],
  key: HasSourceObject["sourceObject"] | undefined
): SourceObjectMessageArgs => {
  // A key the org no longer lists (or any key once source objects are off) reads as the standard
  // object: messages must never show a raw key.
  const listedKey = isNotEmpty(key) && !values.some((item) => item.key === key) ? null : key;
  return {
    label: isEntityTypeSupportingCustomSourceObject(entityType)
      ? getSourceObjectLabel(intl, entityType, values, listedKey)
      : undefined,
    sourceObject: getSourceObjectIntlKey(listedKey),
  };
};

const nounMessage = defineMessage({
  id: "sourceObject.noun",
  defaultMessage:
    "{sourceObject, select, standard {{entityName}} leadCompanies {Lead} siteCompanies {Site} projectCompanies {Project} shopCompanies {Shop} partnerPeople {Partner} leadPeople {Lead} staffMemberPeople {Staff Member} leadPersonnel {Lead Personnel} other {{label}}}",
  description:
    'Name of one record\'s type, shown as a field label. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
});

// The stand-alone noun for one record: its type name, or the entity name for the standard object
export const getSourceObjectDisplayName = (
  intl: IntlShape,
  entityType: EntityType,
  args: SourceObjectMessageArgs
): string =>
  intl.formatMessage(nounMessage, {
    ...args,
    entityName: getEntityTypeDisplayName(intl, entityType, { lowercase: false }),
  });

// Keys the web writes its own ICU arms for, so a subtype name inflects per language.
// Per entity type: a Company lead and a Person lead need not be one noun. Raw keys, not arms.
export const KNOWN_SOURCE_OBJECTS: Readonly<
  Record<EntityTypesSupportingCustomSourceObject, readonly string[]>
> = {
  [EntityType.COMPANY]: ["leadCompanies", "siteCompanies", "projectCompanies", "shopCompanies"],
  [EntityType.PERSON]: ["partnerPeople", "leadPeople", "staffMemberPeople", "leadPersonnel"],
};
